import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';

/**
 * Global gate that checks if the current user is blocked.
 * If blocked: redirects to /blocked WITHOUT signing out.
 * 
 * CRITICAL: We do NOT sign out blocked users because:
 * 1. get_my_block_info requires auth.uid() to fetch block details
 * 2. Swish/Stripe unlock payments require authenticated user
 * 3. RLS policies + backend checks enforce all other restrictions
 * 
 * Security features:
 * - Initial check on user login
 * - Realtime subscription for immediate detection
 * - Broadcast channel listener for server-side block notifications
 * - Debounced check on app focus (only after >60s hidden)
 */

// Minimum time (ms) the app must be hidden before a focus-check is triggered
const MIN_HIDDEN_DURATION = 60_000;

export function BlockedUserGate({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [isChecking, setIsChecking] = useState(false);
  const lastCheckedUserId = useRef<string | null>(null);
  const hiddenSinceRef = useRef<number | null>(null);

  // Keep a ref to the current pathname so the realtime handler — whose
  // closure is frozen to the pathname at subscription time — can branch
  // on where the user actually is right now.
  const pathnameRef = useRef(location.pathname);
  useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);

  // Paths where we don't redirect (already on blocked or auth pages)
  const exemptPaths = ['/blocked', '/auth', '/bankid-callback'];

  const handleBlockedUser = useCallback(() => {
    console.log('[BlockedUserGate] User is blocked, redirecting to /blocked');
    // KRITISKT: Vi loggar INTE ut användaren
    // De behöver vara inloggade för att:
    // 1. get_my_block_info ska fungera (kräver auth.uid())
    // 2. Swish/Stripe betalningar ska kunna genomföras
    // 3. RLS + backend enforces alla andra restriktioner
    navigate('/blocked', { replace: true });
  }, [navigate]);

  // Check if user is blocked
  const checkBlocked = useCallback(async (forceCheck = false) => {
    if (!user?.id) return;
    
    // Skip if on exempt paths
    if (exemptPaths.some(p => location.pathname.startsWith(p))) {
      return;
    }

    // Skip if we already checked this user recently (unless force)
    if (!forceCheck && lastCheckedUserId.current === user.id) {
      return;
    }

    if (isChecking) return;
    setIsChecking(true);

    try {
      console.log('[BlockedUserGate] Checking blocked status for user:', user.id);

      // Canonical source of truth: get_my_block_info() RPC over user_blocks.
      // BlockedPage uses the same RPC, so guard/page can never disagree and
      // produce a redirect loop. profiles_v2.is_blocked is treated as a
      // derived mirror column, not a gating source.
      const { data, error } = await supabase.rpc('get_my_block_info');

      if (error) {
        console.error('[BlockedUserGate] Error checking blocked status:', error);
        return;
      }

      lastCheckedUserId.current = user.id;

      if (Array.isArray(data) && data.length > 0) {
        handleBlockedUser();
      } else {
        console.log('[BlockedUserGate] User is not blocked');
      }
    } catch (err) {
      console.error('[BlockedUserGate] Exception:', err);
    } finally {
      setIsChecking(false);
    }
  }, [user?.id, location.pathname, isChecking, handleBlockedUser]);

  // Reset lastCheckedUserId when leaving exempt paths to force fresh DB check
  // This prevents cache-bypass attacks where stale isBlocked status persists
  useEffect(() => {
    const isOnExemptPath = exemptPaths.some(p => location.pathname.startsWith(p));
    
    if (!isOnExemptPath && lastCheckedUserId.current !== null) {
      // We're navigating away from blocked/auth page - force fresh check
      console.log('[BlockedUserGate] Left exempt path, forcing fresh check');
      lastCheckedUserId.current = null;
    }
  }, [location.pathname]);

  // Initial check when user changes
  useEffect(() => {
    if (!user?.id) {
      lastCheckedUserId.current = null;
      return;
    }
    checkBlocked();
  }, [user?.id, location.pathname, checkBlocked]);

  // Debounced focus check — only fire DB check when the app has been hidden for >60s.
  // Realtime + broadcast handle immediate block detection; this is a safety net
  // for cases where the user had the app backgrounded for a long period.
  useEffect(() => {
    if (!user?.id) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenSinceRef.current = Date.now();
      } else if (document.visibilityState === 'visible') {
        const hiddenDuration = hiddenSinceRef.current
          ? Date.now() - hiddenSinceRef.current
          : 0;
        hiddenSinceRef.current = null;

        if (hiddenDuration >= MIN_HIDDEN_DURATION) {
          console.log(`[BlockedUserGate] App visible after ${Math.round(hiddenDuration / 1000)}s — checking block status`);
          checkBlocked(true);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user?.id, checkBlocked]);

  // Set up realtime subscription for immediate block detection
  useEffect(() => {
    if (!user?.id) return;

    console.log('[BlockedUserGate] Setting up realtime subscription for user:', user.id);

    const channel = supabase
      .channel(`blocked-check-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles_v2',
          filter: `id=eq.${user.id}`,
        },
        async (payload) => {
          console.log('[BlockedUserGate] Realtime update received:', payload);
          const newBlocked = (payload.new as any).is_blocked;

          if (newBlocked === true) {
            // Verify via RPC (canonical source) before redirecting — protects
            // against stale profiles_v2.is_blocked that disagrees with user_blocks.
            const { data } = await supabase.rpc('get_my_block_info');
            if (Array.isArray(data) && data.length > 0) {
              await handleBlockedUser();
            } else {
              console.log('[BlockedUserGate] profiles_v2 flagged blocked but RPC disagrees — ignoring');
            }
          } else if (newBlocked === false && pathnameRef.current === '/blocked') {
            // User just got unblocked and is on blocked page - redirect to org slug
            console.log('[BlockedUserGate] User unblocked, invalidating cache and redirecting');
            // Invalidate permissions cache to clear stale isBlocked: true
            await queryClient.invalidateQueries({ queryKey: ['pwa-user-org'] });
            await queryClient.invalidateQueries({ queryKey: ['user-permissions'] });
            let savedSlugPath = sessionStorage.getItem('last_slug_path') || '/';
            // Normalisera: säkerställ leading slash för absolut navigation
            if (savedSlugPath && savedSlugPath !== '/' && !savedSlugPath.startsWith('/')) {
              savedSlugPath = '/' + savedSlugPath;
            }
            console.log('[BlockedUserGate] Redirecting to:', savedSlugPath);
            navigate(savedSlugPath, { replace: true });
          }
        }
      )
      .subscribe();

    return () => {
      console.log('[BlockedUserGate] Cleaning up realtime subscription');
      supabase.removeChannel(channel);
    };
  }, [user?.id, handleBlockedUser]);

  // Listen for broadcast messages from server when user is blocked
  useEffect(() => {
    if (!user?.id) return;

    console.log('[BlockedUserGate] Setting up broadcast listener for user:', user.id);

    const broadcastChannel = supabase
      .channel(`user-blocked-${user.id}`)
      .on('broadcast', { event: 'user_blocked' }, async (payload) => {
        console.log('[BlockedUserGate] Received blocked broadcast:', payload);
        await handleBlockedUser();
      })
      .subscribe();

    return () => {
      console.log('[BlockedUserGate] Cleaning up broadcast listener');
      supabase.removeChannel(broadcastChannel);
    };
  }, [user?.id, handleBlockedUser]);

  return <>{children}</>;
}
